import * as i0 from '@angular/core';
import { InjectionToken, Injectable, EventEmitter, Component, Inject, Input, Output, ViewChild, ViewEncapsulation, NgModule } from '@angular/core';
import * as i1 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';
import { deepEqual } from 'fast-equals';
import { ReplaySubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import * as i2 from '@ng-bootstrap/ng-bootstrap';
import DLC from '@mhe/air-item-viewer/dist/static/js/main.js';

const DLC_TOKEN = new InjectionToken('AIR');

class AirEmitterService {
    constructor() {
        this.emitters = [];
        this.$emitterSubject = new ReplaySubject();
    }
    add(emitter) {
        if (!this.emitters.includes(emitter)) {
            this.emitters.push(emitter);
            this.$emitterSubject.next(this.emitters);
        }
    }
    remove(emitter) {
        const index = this.emitters.indexOf(emitter);
        if (index !== -1) {
            this.emitters.splice(index, 1);
            this.$emitterSubject.next(this.emitters);
        }
    }
}
AirEmitterService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirEmitterService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
AirEmitterService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirEmitterService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirEmitterService, decorators: [{
            type: Injectable
        }] });

/* eslint-disable no-console */
class ModalComponent {
    constructor(location, modalService, _dom) {
        this.modalService = modalService;
        this._dom = _dom;
        this.cancelButtonText = 'Cancel';
        this.confirmButtonText = 'Confirm';
        this.disableConfirm = false;
        this.showFooter = true;
        this.modalSize = 'md';
        this.focusCloseOnOpen = true;
        this.closeImmediateOnConfirm = false;
        this.close = new EventEmitter();
        this.confirm = new EventEmitter();
        this.cancel = new EventEmitter();
        this._modalDialogEl = null;
        this._ngbModalWindowEl = null;
        location.onPopState(() => {
            this.close.emit();
        });
    }
    ngOnChanges(changes) {
        if (changes['isOpen']?.currentValue) {
            this.openAirModal();
        }
        if (changes['closeImmediateOnConfirm']?.currentValue === false) {
            this.onClose();
        }
    }
    _fixA11y() {
        this._modalDialogEl = this._dom.querySelector('ngb-modal-window  .modal-dialog');
        this._modalDialogEl?.removeAttribute('role');
        this._ngbModalWindowEl = this._dom.querySelector('ngb-modal-window');
        this._ngbModalWindowEl?.setAttribute('aria-labelledby', 'modal-title');
        const modalContent = this._dom.querySelectorAll('ngb-modal-window  #modal-content');
        if (modalContent.length === 1) {
            this._ngbModalWindowEl?.setAttribute('aria-describedby', 'modal-content');
        }
        else if (modalContent.length > 1) {
            console.log('There are duplicate IDs on the page with modal-content');
        }
    }
    openAirModal() {
        this.airModal = this.modalService.open(this.airModalContent, {
            size: this.modalSize,
            fullscreen: 'sm',
        });
        this._fixA11y();
        this.airModal.dismissed.subscribe((reason) => {
            this.onClose();
        });
    }
    onCancel() {
        this.cancel.emit();
        this.onClose();
    }
    onConfirm() {
        this.confirm.emit();
        setTimeout(() => {
            if (!this.closeImmediateOnConfirm) {
                this.onClose();
            }
        });
    }
    onClose() {
        this.close.emit();
        if (this.focusElementOnClose) {
            setTimeout(() => {
                this.focusElementOnClose.nativeElement.focus();
            });
        }
        this.airModal?.close();
    }
}
ModalComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ModalComponent, deps: [{ token: i1.PlatformLocation }, { token: i2.NgbModal }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component });
ModalComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ModalComponent, selector: "air-modal", inputs: { isOpen: "isOpen", focusElementOnClose: "focusElementOnClose", titleText: "titleText", cancelButtonText: "cancelButtonText", confirmButtonText: "confirmButtonText", disableConfirm: "disableConfirm", showFooter: "showFooter", modalSize: "modalSize", focusCloseOnOpen: "focusCloseOnOpen", closeImmediateOnConfirm: "closeImmediateOnConfirm" }, outputs: { close: "close", confirm: "confirm", cancel: "cancel" }, viewQueries: [{ propertyName: "airModalContent", first: true, predicate: ["airModal"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<ng-template #airModal let-modal>\n  <div class=\"modal-header\">\n    <h2 class=\"modal-title\" id=\"modal-title\" translate>{{ titleText }}</h2>\n    <button\n      type=\"button\"\n      class=\"btn-close\"\n      aria-label=\"Close\"\n      (click)=\"onClose()\"\n      [attr.ngbAutofocus]=\"focusCloseOnOpen ? true : null\">\n    </button>\n  </div>\n\n  <div\n    class=\"modal-body p-4\"\n    id=\"modal-content\">\n    <ng-content></ng-content>\n  </div>\n  <div *ngIf=\"showFooter\"\n       class=\"justify-content-end modal-footer position-relative px-3\">\n    <button\n      *ngIf=\"cancelButtonText\"\n      type=\"button\"\n      class=\"action-button btn btn-outline-primary\"\n      (click)=\"onCancel()\">\n      <span class=\"button-text-clickable\" translate\n        >{{ cancelButtonText }}</span\n      >\n    </button>\n\n    <button\n      *ngIf=\"confirmButtonText\"\n      type=\"button\"\n      class=\"action-button btn btn-primary\"\n      [disabled]=\"disableConfirm\"\n      (click)=\"onConfirm()\">\n      <span class=\"button-text-clickable\" translate\n        >{{ confirmButtonText }}</span\n      >\n    </button>\n  </div>\n</ng-template>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'air-modal', template: "<ng-template #airModal let-modal>\n  <div class=\"modal-header\">\n    <h2 class=\"modal-title\" id=\"modal-title\" translate>{{ titleText }}</h2>\n    <button\n      type=\"button\"\n      class=\"btn-close\"\n      aria-label=\"Close\"\n      (click)=\"onClose()\"\n      [attr.ngbAutofocus]=\"focusCloseOnOpen ? true : null\">\n    </button>\n  </div>\n\n  <div\n    class=\"modal-body p-4\"\n    id=\"modal-content\">\n    <ng-content></ng-content>\n  </div>\n  <div *ngIf=\"showFooter\"\n       class=\"justify-content-end modal-footer position-relative px-3\">\n    <button\n      *ngIf=\"cancelButtonText\"\n      type=\"button\"\n      class=\"action-button btn btn-outline-primary\"\n      (click)=\"onCancel()\">\n      <span class=\"button-text-clickable\" translate\n        >{{ cancelButtonText }}</span\n      >\n    </button>\n\n    <button\n      *ngIf=\"confirmButtonText\"\n      type=\"button\"\n      class=\"action-button btn btn-primary\"\n      [disabled]=\"disableConfirm\"\n      (click)=\"onConfirm()\">\n      <span class=\"button-text-clickable\" translate\n        >{{ confirmButtonText }}</span\n      >\n    </button>\n  </div>\n</ng-template>\n" }]
        }], ctorParameters: function () { return [{ type: i1.PlatformLocation }, { type: i2.NgbModal }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; }, propDecorators: { isOpen: [{
                type: Input
            }], focusElementOnClose: [{
                type: Input
            }], titleText: [{
                type: Input
            }], cancelButtonText: [{
                type: Input
            }], confirmButtonText: [{
                type: Input
            }], disableConfirm: [{
                type: Input
            }], showFooter: [{
                type: Input
            }], modalSize: [{
                type: Input
            }], focusCloseOnOpen: [{
                type: Input
            }], closeImmediateOnConfirm: [{
                type: Input
            }], close: [{
                type: Output
            }], confirm: [{
                type: Output
            }], cancel: [{
                type: Output
            }], airModalContent: [{
                type: ViewChild,
                args: ['airModal']
            }] } });

// Delay time in milliseconds to prevent too many frequent calls to updateJson.
const UPDATE_DELAY = 500;
class AirItemComponent {
    constructor(dlc, airEmitterService, changeDetectionRef) {
        this.dlc = dlc;
        this.airEmitterService = airEmitterService;
        this.changeDetectionRef = changeDetectionRef;
        this.preserveAirState = false;
        this.airVersion = 2;
        this.viewMode = 'itemPreview';
        this.debugMode = false;
        this.transform = false;
        this.showRationales = true;
        this.viewExplanationButton = false;
        this.showExampleResponse = false;
        this.showExplanation = false;
        this.showHint = false;
        this.showExample = false;
        this.scrollParent = null;
        this.aleksConfig = null;
        this.enableSpinner = false;
        this.disableUploads = false;
        this.response = new EventEmitter();
        this.closeHint = new EventEmitter();
        this.closeExample = new EventEmitter();
        this.itemDefinitionChanged = new Subject();
        this.itemDefinitionChanged
            .pipe(debounceTime(UPDATE_DELAY))
            .pipe(distinctUntilChanged())
            .subscribe(() => {
            this.updateItemDefinition();
        });
    }
    ngAfterViewInit() {
        if (!this.air) {
            throw new Error('Cannot render item with undefined AIR');
        }
        if (!this.itemType) {
            throw new Error('Cannot render item with undefined itemType');
        }
        this.initializeRenderer();
    }
    emitResourceEvent(changes, propertyName) {
        const changedProperty = changes[propertyName];
        if (changedProperty) {
            const currentPropertyValue = changedProperty.currentValue;
            const previousPropertyValue = changedProperty.previousValue;
            if (previousPropertyValue !== undefined && currentPropertyValue !== previousPropertyValue) {
                this.eventEmitter.emitEvent(propertyName, currentPropertyValue);
            }
        }
    }
    viewModeChanges(viewModeChange) {
        if (viewModeChange) {
            const currentModeValue = viewModeChange.currentValue;
            const previousModeValue = viewModeChange.previousValue;
            if (previousModeValue && currentModeValue !== previousModeValue) {
                // This check is added for external scoring by Avalon. In this case we
                // want to call updateItemDefinition to add responseCorrectness to the json.
                if (!this.air.responseCorrectness) {
                    this.eventEmitter.switchMode(currentModeValue);
                    return;
                }
            }
            this.itemDefinitionChanged.next(this.getFullDefinition());
        }
    }
    itemDefinitionChanges(change) {
        if (change && change.previousValue && !deepEqual(change.previousValue, change.currentValue)) {
            this.itemDefinitionChanged.next(this.getFullDefinition());
        }
    }
    showRationalesChanges(change) {
        if (change && typeof change.previousValue !== undefined && change.currentValue !== change.previousValue) {
            this.itemDefinitionChanged.next(this.getFullDefinition());
            return true;
        }
        return false;
    }
    hintChanges(changes) {
        if (changes.showHint && changes.showHint.currentValue) {
            this.resourceChanges('hint', this.hintEmitter, this.airHint);
        }
    }
    exampleChanges(changes) {
        if (changes.showExample && changes.showExample.currentValue) {
            this.resourceChanges('example', this.exampleEmitter, this.airExample);
        }
    }
    resourceChanges(type, emitter, element) {
        const resourceDefinition = this.getResourceDefinition(type);
        const nativeElem = element && element.nativeElement ? element.nativeElement : null;
        this.dlc.load(nativeElem, resourceDefinition).then((airEmitter) => {
            this[`${type}Emitter`] = airEmitter;
        });
    }
    ngOnChanges(changes) {
        if (!this.eventEmitter) {
            return;
        }
        this.emitResourceEvent(changes, 'showExplanation');
        this.emitResourceEvent(changes, 'showExampleResponse');
        const showRationalesChanged = this.showRationalesChanges(changes.showRationales);
        if (!showRationalesChanged) {
            this.viewModeChanges(changes.viewMode);
        }
        this.itemDefinitionChanges(changes.air);
        this.itemDefinitionChanges(changes.apiConfig);
        this.itemDefinitionChanges(changes.aleksConfig);
        this.changeDetectionRef.detectChanges();
        this.hintChanges(changes);
        this.exampleChanges(changes);
    }
    ngOnDestroy() {
        this.airEmitterService.remove(this.eventEmitter);
        if (this.eventEmitter) {
            this.eventEmitter.removeListener();
            this.eventEmitter.destroy();
        }
    }
    getFullDefinition() {
        return {
            itemDefinition: this.itemDefinition(),
            apiConfig: this.apiConfig,
            version: this.airVersion,
            mode: this.viewMode,
            scrollParent: this.scrollParent,
            showRationales: this.showRationales,
            viewExplanationButton: this.viewExplanationButton,
            aleksConfig: this.aleksConfig,
            enableSpinner: this.enableSpinner,
            disableUploads: this.disableUploads,
            wirisMathUrl: this.wirisMathUrl,
        };
    }
    getResourceDefinition(resourceType) {
        const resource = this.air && this.air.resources && this.air.resources[resourceType];
        if (resource) {
            return {
                itemDefinition: {
                    ...resource,
                    item_type: resourceType,
                },
                enableSpinner: this.enableSpinner,
                disableUploads: this.disableUploads,
                version: this.airVersion,
                wirisMathUrl: this.wirisMathUrl,
                mode: 'testing',
            };
        }
        return;
    }
    itemDefinition() {
        if (this.transform) {
            return this.transformAir();
        }
        return {
            ...this.air,
            item_type: this.itemType,
        };
    }
    transformAir() {
        const air = this.air;
        const itemDetails = { ...air };
        delete itemDetails.responseCorrectness;
        delete itemDetails.response;
        delete itemDetails.type;
        delete itemDetails.resources;
        const item = {
            item_details: itemDetails,
            item_type: this.getTransformedItemName(air.type) || this.itemType,
            type: 'item',
            response: air.response,
            responseCorrectness: air.responseCorrectness,
            resources: air.resources,
        };
        return item;
    }
    getTransformedItemName(type) {
        return (type &&
            type.replace(/_([a-z])/g, function (m, w) {
                return w.toUpperCase();
            }));
    }
    attachEventListeners() {
        this.eventEmitter.on('responseUpdated', () => this.emitLatestResponse());
    }
    initializeRenderer() {
        if (this.debugMode) {
            console.log('AIR renderer initialized with the following JSON:');
            console.log(JSON.stringify(this.getFullDefinition(), null, 2));
        }
        const nativeElem = this.airRoot && this.airRoot.nativeElement ? this.airRoot.nativeElement : null;
        this.dlc.load(nativeElem, this.getFullDefinition()).then((eventEmitter) => {
            this.airEmitterService.add(eventEmitter);
            this.eventEmitter = eventEmitter;
            this.emitLatestResponse();
            this.attachEventListeners();
        });
    }
    emitLatestResponse() {
        this.eventEmitter.getJson(this.airVersion).then((definition) => {
            this.response.emit(JSON.parse(definition).itemDefinition);
        });
    }
    updateItemDefinition() {
        const fullDefinition = this.getFullDefinition();
        if (this.debugMode) {
            console.log('AIR renderer updated with the following JSON:');
            console.log(JSON.stringify(fullDefinition, null, 2));
        }
        this.eventEmitter.updateJSON(fullDefinition, this.preserveAirState);
    }
}
AirItemComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirItemComponent, deps: [{ token: DLC_TOKEN }, { token: AirEmitterService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
AirItemComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AirItemComponent, selector: "aa-air-item", inputs: { air: "air", itemType: "itemType", preserveAirState: "preserveAirState", airVersion: "airVersion", viewMode: "viewMode", debugMode: "debugMode", transform: "transform", showRationales: "showRationales", viewExplanationButton: "viewExplanationButton", showExampleResponse: "showExampleResponse", showExplanation: "showExplanation", showHint: "showHint", showExample: "showExample", scrollParent: "scrollParent", aleksConfig: "aleksConfig", apiConfig: "apiConfig", enableSpinner: "enableSpinner", disableUploads: "disableUploads", wirisMathUrl: "wirisMathUrl" }, outputs: { response: "response", closeHint: "closeHint", closeExample: "closeExample" }, viewQueries: [{ propertyName: "airRoot", first: true, predicate: ["airRoot"], descendants: true, static: true }, { propertyName: "airHint", first: true, predicate: ["airHint"], descendants: true }, { propertyName: "airExample", first: true, predicate: ["airExample"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div class=\"air-item-container\">\n  <div class=\"air-item\" #airRoot></div>\n  <air-modal\n    *ngIf=\"showHint\"\n    titleText=\"{{'Hint'}}\"\n    [isOpen]=\"showHint\"\n    [showFooter]=\"false\"\n    (show)=\"false\"\n    (close)=\"closeHint.emit()\"\n    (cancel)=\"closeHint.emit()\">\n    <div class=\"air-item\" #airHint></div>\n  </air-modal>\n  <air-modal\n    *ngIf=\"showExample\"\n    titleText=\"{{'Example'}}\"\n    [isOpen]=\"showExample\"\n    [showFooter]=\"false\"\n    (show)=\"false\"\n    (close)=\"closeExample.emit()\"\n    (cancel)=\"closeExample.emit()\">\n    <div class=\"air-item\" #airExample></div>\n  </air-modal>\n</div>\n", styles: ["aa-preview-modal .modal-dialog{width:700px}@media screen and (max-width: 768px){aa-preview-modal .modal-dialog{margin:0 auto;width:85%;clear:both}}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: ModalComponent, selector: "air-modal", inputs: ["isOpen", "focusElementOnClose", "titleText", "cancelButtonText", "confirmButtonText", "disableConfirm", "showFooter", "modalSize", "focusCloseOnOpen", "closeImmediateOnConfirm"], outputs: ["close", "confirm", "cancel"] }], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-air-item', encapsulation: ViewEncapsulation.None, template: "<div class=\"air-item-container\">\n  <div class=\"air-item\" #airRoot></div>\n  <air-modal\n    *ngIf=\"showHint\"\n    titleText=\"{{'Hint'}}\"\n    [isOpen]=\"showHint\"\n    [showFooter]=\"false\"\n    (show)=\"false\"\n    (close)=\"closeHint.emit()\"\n    (cancel)=\"closeHint.emit()\">\n    <div class=\"air-item\" #airHint></div>\n  </air-modal>\n  <air-modal\n    *ngIf=\"showExample\"\n    titleText=\"{{'Example'}}\"\n    [isOpen]=\"showExample\"\n    [showFooter]=\"false\"\n    (show)=\"false\"\n    (close)=\"closeExample.emit()\"\n    (cancel)=\"closeExample.emit()\">\n    <div class=\"air-item\" #airExample></div>\n  </air-modal>\n</div>\n", styles: ["aa-preview-modal .modal-dialog{width:700px}@media screen and (max-width: 768px){aa-preview-modal .modal-dialog{margin:0 auto;width:85%;clear:both}}\n"] }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DLC_TOKEN]
                }] }, { type: AirEmitterService }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { air: [{
                type: Input
            }], itemType: [{
                type: Input
            }], preserveAirState: [{
                type: Input
            }], airVersion: [{
                type: Input
            }], viewMode: [{
                type: Input
            }], debugMode: [{
                type: Input
            }], transform: [{
                type: Input
            }], showRationales: [{
                type: Input
            }], viewExplanationButton: [{
                type: Input
            }], showExampleResponse: [{
                type: Input
            }], showExplanation: [{
                type: Input
            }], showHint: [{
                type: Input
            }], showExample: [{
                type: Input
            }], scrollParent: [{
                type: Input
            }], aleksConfig: [{
                type: Input
            }], apiConfig: [{
                type: Input
            }], enableSpinner: [{
                type: Input
            }], disableUploads: [{
                type: Input
            }], wirisMathUrl: [{
                type: Input
            }], response: [{
                type: Output
            }], closeHint: [{
                type: Output
            }], closeExample: [{
                type: Output
            }], airRoot: [{
                type: ViewChild,
                args: ['airRoot', { static: true }]
            }], airHint: [{
                type: ViewChild,
                args: ['airHint']
            }], airExample: [{
                type: ViewChild,
                args: ['airExample']
            }] } });

class AirModalModule {
}
AirModalModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirModalModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AirModalModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: AirModalModule, declarations: [ModalComponent], imports: [CommonModule], exports: [ModalComponent] });
AirModalModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirModalModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirModalModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [ModalComponent],
                    exports: [ModalComponent],
                }]
        }] });

function getDlc() {
    return DLC;
}
class AirRenderingModule {
}
AirRenderingModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirRenderingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AirRenderingModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: AirRenderingModule, declarations: [AirItemComponent], imports: [CommonModule, AirModalModule], exports: [AirItemComponent] });
AirRenderingModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirRenderingModule, providers: [AirEmitterService, { provide: DLC_TOKEN, useFactory: getDlc }], imports: [CommonModule, AirModalModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirRenderingModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, AirModalModule],
                    declarations: [AirItemComponent],
                    providers: [AirEmitterService, { provide: DLC_TOKEN, useFactory: getDlc }],
                    exports: [AirItemComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AirEmitterService, AirItemComponent, AirModalModule, AirRenderingModule, ModalComponent };
